import { PICTURE_DEBUG, PICTURE_TYPES, SERVER_URL } from 'Global/constants'
import { getCurrentAndUpcoming, isAvailable, isAvailableRange, userHasReservations } from 'Global/Helpers/reservationHelpers'
import { hasPermission } from 'Components/Permissions/utils'

const fullDateStringRe = /(\d{1,2})\.(\d{1,2})\.(\d{2,4})/

export const defaultExtendedFilters = {
  active: false,
  availability: 'all',
  advisor: [],
  color: [],
  dateIndexed: { max: '', min: '' },
  dateInvoice: { max: '', min: '' },
  datePrimaryPicture: { max: '', min: '' },
  dateReceipt: { max: '', min: '' },
  dateReservation: { max: '', min: '' },
  description: '',
  isMasterpiece: 'all',
  report: false,
  requested: 'all',
  reservationEvent: [],
  reservationUser: [],
  sizeQm: { max: '', min: '' },
  source: [],
}

export const defaultFilters = {
  customer: '',
  dessin: [],
  height: { max: '', min: '' },
  idOrName: '',
  orderYear: { max: '', min: '' },
  producer: [],
  quality: [],
  status: [],
  width: { max: '', min: '' },
}

/**
 * Returns the dessin description from a dessin string.
 * @param {string} dessin
 * @returns {string} Dessin Name
 */
export const getDessinName = dessin => {
  switch (parseInt(dessin, 10)) {
    case 1:
      return 'Fringes: Yes'
    case 2:
      return 'Fringes: No'
    case 3:
      return 'Edges: Finished'
    case 4:
      return 'Edges: Open'
    case 5:
      return 'Fringes/Kelim: Like Original'
    case 6:
      return 'Kelim'
    case 7:
      return 'With Tassels'
    case 8:
      return 'Without Tassels'

    default:
      return ''
  }
}

/**
 * Returns verbose source string.
 * @param {string} source
 * @returns {string} Source String
 */
export const getPictureSourceString = source => {
  switch (source) {
    case 'designer':
      return 'Designer'

    case 'import':
      return 'Imported'

    case 'mobile':
      return 'Mobile'

    case 'shipping':
      return 'Shipping'

    case 'station':
      return 'Photo Station'

    default:
      return ''
  }
}

/**
 * Returns the path of a picturefile of the provided type from a picture object.
 * @param {Object} picture
 * @param {string} type
 * @returns {string} Picture Path
 */
export const getPicturefilePath = (picture, type) => {
  if (picture) {
    if (picture.files) {
      const pictureFile = picture.files.find(f => f.type === type)

      if (pictureFile) {
        return `/static/pictures/${picture.rug}/${pictureFile.fileName}`
      } else if (type !== PICTURE_TYPES.ORIGINAL_JPG) {
        return getPicturefilePath(picture, PICTURE_TYPES.ORIGINAL_JPG)
      }
    }
  }

  return `/static/grids/no_rugs_found.png`
}

/**
 * Returns the path of a picturefile of the provided type from a picture object.
 * @param {Object} picture
 * @param {string} type
 * @returns {string} Picture Path
 */
export const getFullPicturefilePath = (picture, type) => {
  if (picture) {
    if (picture.files) {
      const pictureFile = picture.files.find(f => f.type === type)

      if (pictureFile) {
        if (PICTURE_DEBUG) {
          return PICTURE_DEBUG
        }

        return `${SERVER_URL}/static/pictures/${picture.rug}/${pictureFile.fileName}`
      } else if (type !== PICTURE_TYPES.ORIGINAL_JPG) {
        return getFullPicturefilePath(picture, PICTURE_TYPES.ORIGINAL_JPG)
      }
    }
  }

  return `${SERVER_URL}/static/grids/no_rugs_found.png`
}

/**
 * Returns status object from a rug object.
 * @param {Object} rug
 * @returns {Object} Status Object
 */
export const getStatus = rug => {
  const checkOnStock = rug => {
    if (rug.rsvMenge !== null) {
      if (rug.sizeQm < 1) {
        if (rug.rsvMenge > 0) {
          return {
            color: 'yellow',
            icon: 'external',
            permissionKey: 'rug.status.onConsignment',
            text: 'On Consignment',
          }
        }
      }

      if (rug.rsvMenge >= 1) {
        return {
          color: 'yellow',
          icon: 'external',
          permissionKey: 'rug.status.onConsignment',
          text: 'On Consignment',
        }
      }
    }

    if (rug.quantity !== null) {
      if (rug.quantity < rug.sizeQm * 0.3) {
        return {
          color: 'red',
          icon: 'x',
          permissionKey: 'rug.status.sold',
          text: 'Sold',
        }
      }
    }

    return {
      color: 'green',
      icon: 'check',
      permissionKey: 'rug.status.inStock',
      text: 'In Stock',
    }
  }

  if (rug) {
    if (rug.status) {
      switch (rug.status) {
        case 'on stock':
          return checkOnStock(rug)

        case 'knotting':
          return {
            color: 'grey',
            icon: 'share alternate',
            permissionKey: 'rug.status.knotting',
            text: 'Knotting',
          }

        default:
          if (rug.belReart === 'AU') {
            return {
              color: 'yellow',
              icon: 'external',
              permissionKey: 'rug.status.onConsignment',
              text: 'On Consignment',
            }
          }

          if (rug.belReart === 'AB') {
            return {
              color: 'orange',
              icon: 'bookmark',
              permissionKey: 'rug.status.confirmed',
              text: 'Confirmed',
            }
          }

          if (rug.belReart === 'KR') {
            return {
              color: 'red',
              icon: 'x',
              permissionKey: 'rug.status.sold',
              text: 'Sold',
            }
          }
      }
    }
  }

  return {
    color: 'black',
    icon: 'warning sign',
    permissionKey: 'rug.status.unknown',
    text: 'Unknown',
  }
}

/**
 * Returns whether a picture has a picturefile of the provided type.
 * @param {Object} picture
 * @param {string} type
 * @returns {bool} bool
 */
export const hasPictureType = (picture, type) => {
  if (picture) {
    if (picture.files) {
      const pictureFile = picture.files.find(file => file.type === type)

      if (pictureFile) {
        return true
      }
    }
  }

  return false
}

export const makeExtendedFilterRugs = extendedFilters => rug => {
  const mergedExtendedFilters = {
    ...defaultExtendedFilters,
    ...extendedFilters,
  }

  const {
    advisor,
    availability,
    dateReceipt,
    dateReservation,
    description,
    isMasterpiece,
    requested,
    reservationEvent,
    reservationUser,
    sizeQm,
    source,
  } = mergedExtendedFilters

  if (isMasterpiece === 'yes') {
    if (!rug.isMasterpiece) {
      return false
    }
  }

  if (isMasterpiece === 'no') {
    if (rug.isMasterpiece) {
      return false
    }
  }

  if (advisor.length > 0) {
    let keep = false

    for (const a of advisor) {
      if (rug.advisor.toLowerCase().includes(a.toLowerCase().trim())) {
        keep = true
      }
    }

    if (keep === false) {
      return false
    }
  }

  if (reservationEvent.length > 0) {
    let keep = false

    for (const e of reservationEvent) {
      for (const r of rug.reservations) {
        if (r) {
          if (r.event) {
            if (r.event.name.toLowerCase() === e.toLowerCase()) {
              keep = true
            }
          }
        }
      }
    }

    if (!keep) {
      return false
    }
  }

  if (reservationUser.length > 0) {
    let keep = false

    for (const u of reservationUser.map(u => ({ id: u }))) {
      if (userHasReservations(u, rug.reservations)) {
        keep = true
      }
    }

    if (!keep) {
      return false
    }
  }

  switch (availability) {
    case 'available':
      if (dateReservation.min || dateReservation.max) {
        if (!isAvailableRange(rug.reservations, dateReservation.min, dateReservation.max)) {
          return false
        }
      } else if (!isAvailable(rug.reservations)) {
        return false
      }
      break

    case 'customPeriod':
      if (dateReservation.min || dateReservation.max) {
        if (isAvailableRange(rug.reservations, dateReservation.min, dateReservation.max)) {
          return false
        }
      }

      break

    case 'onHold':
      if (isAvailable(rug.reservations)) {
        return false
      }
      break

    case 'currentAndUpcoming':
      const currentAndUpcoming = getCurrentAndUpcoming(rug.reservations)

      if (currentAndUpcoming.length === 0) {
        return false
      }
      break

    default:
      break
  }

  if (dateReceipt.min) {
    const match = fullDateStringRe.exec(dateReceipt.min)

    if (match) {
      const dateReceiptMin = new Date(`${match[3]}-${match[2]}-${match[1]}`)

      if (new Date(rug.dateReceipt) < dateReceiptMin) {
        return false
      }
    } else {
      if (new Date(rug.dateReceipt).getFullYear() < parseInt(dateReceipt.min, 10)) {
        return false
      }
    }
  }

  if (dateReceipt.max) {
    const match = fullDateStringRe.exec(dateReceipt.max)

    if (match) {
      const dateReceiptMax = new Date(`${match[3]}-${match[2]}-${match[1]}`)

      if (new Date(rug.dateReceipt) > dateReceiptMax) {
        return false
      }
    } else {
      if (new Date(rug.dateReceipt).getFullYear() > parseInt(dateReceipt.max, 10)) {
        return false
      }
    }
  }

  if (description) {
    if (!rug.description.toLowerCase().includes(description.toLowerCase())) {
      return false
    }
  }

  if (parseFloat(sizeQm.min) > 0) {
    if (rug.sizeQm < parseFloat(sizeQm.min)) {
      return false
    }
  }

  if (parseFloat(sizeQm.max) > 0) {
    if (rug.sizeQm > parseFloat(sizeQm.max)) {
      return false
    }
  }

  switch (requested) {
    case 'requested':
      return rug.requests.filter(r => !r.deleted && !r.fulfilled).length > 0

    case 'notRequested':
      return rug.requests.filter(r => !r.deleted && !r.fulfilled).length === 0

    default:
      break
  }

  if (dateReceipt.min) {
    const match = fullDateStringRe.exec(dateReceipt.min)

    if (match) {
      const dateReceiptMin = new Date(`${match[3]}-${match[2]}-${match[1]}`)

      if (new Date(rug.dateReceipt) < dateReceiptMin) {
        return false
      }
    } else {
      if (new Date(rug.dateReceipt).getFullYear() < parseInt(dateReceipt.min, 10)) {
        return false
      }
    }
  }

  if (source.length > 0) {
    let keep = false

    for (const src of source) {
      if (rug.pictureSource === src) {
        keep = true
      }
    }

    if (keep === false) {
      return false
    }
  }

  return true
}

export const makeFilterRugs = (filters, userPermissions) => rug => {
  const mergedFilters = { ...defaultFilters, ...filters }

  const { customer, dessin, height, idOrName, orderYear, producer, quality, status, width } = mergedFilters

  const rugStatus = getStatus(rug)

  if (userPermissions) {
    if (!hasPermission(rugStatus.permissionKey, userPermissions)) {
      return false
    }
  }

  if (customer) {
    if (!rug.belCustomer.toLowerCase().includes(customer.toLowerCase())) {
      return false
    }
  }

  if (dessin.length > 0) {
    let keep = false

    for (const d of dessin) {
      if (rug.dessin === d) {
        keep = true
      }
    }

    if (keep === false) {
      return false
    }
  }

  if (parseInt(height.min, 10) > 0) {
    if (rug.sizeH < parseInt(height.min, 10)) {
      return false
    }
  }

  if (parseInt(height.max, 10) > 0) {
    if (rug.sizeH > parseInt(height.max, 10)) {
      return false
    }
  }

  if (idOrName) {
    if (parseInt(idOrName, 10)) {
      if (!rug.id.toString().startsWith(idOrName)) {
        return false
      }
    } else {
      if (!rug.name.toLowerCase().includes(idOrName.toLowerCase())) {
        return false
      }
    }
  }

  if (orderYear.min) {
    const match = fullDateStringRe.exec(orderYear.min)

    if (match) {
      const orderYearMin = new Date(`${match[3]}-${match[2]}-${match[1]}`)

      if (new Date(rug.dateOrder) < orderYearMin) {
        return false
      }
    } else {
      if (new Date(rug.dateOrder).getFullYear() < parseInt(orderYear.min, 10)) {
        return false
      }
    }
  }

  if (orderYear.max) {
    const match = fullDateStringRe.exec(orderYear.max)

    if (match) {
      const orderYearMax = new Date(`${match[3]}-${match[2]}-${match[1]}`)

      if (new Date(rug.dateOrder) > orderYearMax) {
        return false
      }
    } else {
      if (new Date(rug.dateOrder).getFullYear() > parseInt(orderYear.max, 10)) {
        return false
      }
    }
  }

  if (producer.length > 0) {
    let keep = false

    for (const p of producer) {
      if (rug.producerCode === p) {
        keep = true
      }
    }

    if (keep === false) {
      return false
    }
  }

  if (quality.length > 0) {
    let keep = false

    for (const q of quality) {
      if (rug.knotCount === q) {
        keep = true
      }
    }

    if (keep === false) {
      return false
    }
  }

  if (status.length > 0) {
    let keep = false

    for (const s of status) {
      switch (s) {
        case 'INSTOCK':
          if (rugStatus.text === 'In Stock') {
            keep = true
          }

          break

        case 'CONSIGNMENT':
          if (rugStatus.text === 'On Consignment') {
            keep = true
          }
          break

        case 'RESERVED':
          if (rugStatus.text === 'Confirmed') {
            keep = true
          }
          break

        case 'SOLD':
          if (rugStatus.text === 'Sold') {
            keep = true
          }
          break

        case 'KNOTTING':
          if (rug.status) {
            if (rugStatus.text === 'Knotting') {
              keep = true
            }
          }
          break

        case 'UNKNOWN':
          if (rug.status) {
            if (rugStatus.text === 'Unknown') {
              keep = true
            }
          }
          break

        default:
          return false
      }
    }

    if (keep === false) {
      return false
    }
  }

  if (parseInt(width.min, 10) > 0) {
    if (rug.sizeW < parseInt(width.min, 10)) {
      return false
    }
  }

  if (parseInt(width.max, 10) > 0) {
    if (rug.sizeW > parseInt(width.max, 10)) {
      return false
    }
  }

  return true
}

export const makeSortRugs = (sortBy, sortOrder) => (a, b) => {
  switch (sortBy) {
    case 'customer': {
      if (sortOrder === 'ascending') {
        return a.belCustomer.localeCompare(b.belCustomer)
      } else {
        return b.belCustomer.localeCompare(a.belCustomer)
      }
    }

    case 'datePicture': {
      if (sortOrder === 'ascending') {
        return new Date(a.datePicture) - new Date(b.datePicture)
      } else {
        return new Date(b.datePicture) - new Date(a.datePicture)
      }
    }

    case 'dateOrder':
      if (sortOrder === 'ascending') {
        return new Date(a.dateOrder) - new Date(b.dateOrder)
      } else {
        return new Date(b.dateOrder) - new Date(a.dateOrder)
      }

    case 'dateReceipt':
      if (sortOrder === 'ascending') {
        return new Date(a.dateReceipt) - new Date(b.dateReceipt)
      } else {
        return new Date(b.dateReceipt) - new Date(a.dateReceipt)
      }

    case 'dateScanned':
      if (sortOrder === 'ascending') {
        return new Date(a.dateScanned) - new Date(b.dateScanned)
      } else {
        return new Date(b.dateScanned) - new Date(a.dateScanned)
      }

    case 'dessin': {
      if (sortOrder === 'ascending') {
        return a.dessin.localeCompare(b.dessin)
      } else {
        return b.dessin.localeCompare(a.dessin)
      }
    }

    case 'id': {
      if (sortOrder === 'ascending') {
        return a.id - b.id
      } else {
        return b.id - a.id
      }
    }

    case 'name': {
      if (sortOrder === 'ascending') {
        return a.name.localeCompare(b.name)
      } else {
        return b.name.localeCompare(a.name)
      }
    }

    case 'pictureCount': {
      if (sortOrder === 'ascending') {
        return a.pictureCount - b.pictureCount
      } else {
        return b.pictureCount - a.pictureCount
      }
    }

    case 'pictureSource': {
      if (sortOrder === 'ascending') {
        return a.pictureSource.localeCompare(b.pictureSource)
      } else {
        return b.pictureSource.localeCompare(a.pictureSource)
      }
    }

    case 'producer': {
      if (sortOrder === 'ascending') {
        return a.producerCode.localeCompare(b.producerCode)
      } else {
        return b.producerCode.localeCompare(a.producerCode)
      }
    }

    case 'quality': {
      if (sortOrder === 'ascending') {
        return a.knotCount.localeCompare(b.knotCount)
      } else {
        return b.knotCount.localeCompare(a.knotCount)
      }
    }

    case 'sizeQm': {
      if (sortOrder === 'ascending') {
        return a.sizeQm - b.sizeQm
      } else {
        return b.sizeQm - a.sizeQm
      }
    }

    case 'status': {
      if (sortOrder === 'ascending') {
        return getStatus(a).text.localeCompare(getStatus(b).text)
      } else {
        return getStatus(b).text.localeCompare(getStatus(a).text)
      }
    }

    default:
      return new Date(b.dateUpdated) - new Date(a.dateUpdated)
  }
}

/**
 * Updates pictures to set the new primary.
 * @param {number} newPrimaryId
 * @param {Object} pictures
 * @returns {Object} Updated Pictures
 */
export const updatePrimaryPicture = (newPrimaryId, pictures) => {
  const newPictures = pictures.map(picture => {
    if (picture.id === newPrimaryId) {
      return {
        ...picture,
        primary: true,
      }
    }

    return {
      ...picture,
      primary: false,
    }
  })

  return newPictures
}

/*
  Cheater + 1500
  Designer + 300
  Fotostation = 1000
  Mobile | Lager = 500
  Revision + (1 * Revisionen)
*/
